import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { brandIdentity } from '../theme/brandIdentity';
import Logo from './Logo';
import SimuladorVista from './SimuladorVista';
import './GestionPreguntas.css';
import './GestionPreguntas-collections.css';

function GestionPreguntas() {
  const navigate = useNavigate();

  // Opciones predefinidas de colores para respuestas
  const opcionesColores = [
    {
      id: 'neutral',
      nombre: 'Respuesta Neutral',
      descripcion: 'Color estándar para respuestas normales',
      color: '#E5E7EB',
      icono: '⚪'
    },
    {
      id: 'positiva',
      nombre: 'Respuesta Positiva',
      descripcion: 'Para respuestas que indican buen estado',
      color: '#10B981',
      icono: '🟢'
    },
    {
      id: 'negativa',
      nombre: 'Respuesta Negativa',
      descripcion: 'Para respuestas que indican problemas',
      color: '#EF4444',
      icono: '🔴'
    },
    {
      id: 'personalizado',
      nombre: 'Color Personalizado',
      descripcion: 'Selecciona un color personalizado',
      color: '#6366F1',
      icono: '🎨'
    }
  ];

  // Estados para gestión de colecciones
  const [colecciones, setColecciones] = useState([]);
  const [coleccionActiva, setColeccionActiva] = useState(null);
  const [cargandoColecciones, setCargandoColecciones] = useState(false);
  const [errorColecciones, setErrorColecciones] = useState(false);
  const [modoEdicion, setModoEdicion] = useState('nuevo'); // 'nuevo' | 'editando'

  // Estados para categorías
  const [categorias, setCategorias] = useState([]);
  const [cargandoCategorias, setCargandoCategorias] = useState(false);
  const [errorCategorias, setErrorCategorias] = useState(false);
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState('');

  // Estados para modal de nueva colección
  const [mostrarModalNuevaColeccion, setMostrarModalNuevaColeccion] = useState(false);
  const [datosNuevaColeccion, setDatosNuevaColeccion] = useState({
    nombre: '',
    descripcion: '',
    categoria: ''
  });
  const [erroresModal, setErroresModal] = useState({});
  
  // Estados existentes
  const [tituloGeneral, setTituloGeneral] = useState('');
  const [descripcionGeneral, setDescripcionGeneral] = useState('');
  const [contadorImagenes, setContadorImagenes] = useState(0); // Contador para IDs únicos de imágenes
  const [preguntas, setPreguntas] = useState([{
    id: 1,
    titulo: '',
    pregunta: '',
    ejemplos: '',
    imagenes: [],
    respuestas: [
      { id: Date.now(), texto: '', puntos: 0, gradoDirecto: '', accionSiguiente: 'solo_puntos', button_color: '#E5E7EB' }
    ]
  }]);
  
  // Configuración de gradificación
  const [configuracionGradificacion, setConfiguracionGradificacion] = useState({
    tipoGradificacion: 'puntajes', // 'puntajes', 'grados_directos', 'hibrida'
    grados: [
      { id: 'A', nombre: 'Grado A', descripcion: 'Excelente estado', color: '#10B981' },
      { id: 'B', nombre: 'Grado B', descripcion: 'Buen estado', color: '#F59E0B' },
      { id: 'C', nombre: 'Grado C', descripcion: 'Estado regular', color: '#EF4444' }
    ],
    thresholds: [
      { gradoId: 'A', min: 0, max: 5 },
      { gradoId: 'B', min: 6, max: 15 },
      { gradoId: 'C', min: 16, max: 999 }
    ]
  });
  const [preguntaActiva, setPreguntaActiva] = useState(0);
  const [vistaActiva, setVistaActiva] = useState('colecciones'); // 'colecciones', 'preguntas', 'configuracion'
  const [mostrarPanelColecciones, setMostrarPanelColecciones] = useState(true);
  const [errores, setErrores] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [mostrarConfirmacion, setMostrarConfirmacion] = useState(false);
  const [mostrarSimulador, setMostrarSimulador] = useState(false);
  const fileInputRef = useRef(null);
  
  // Cargar colecciones y categorías al montar el componente
  useEffect(() => {
    cargarColecciones();
    cargarCategorias();
  }, []);

  // Función para obtener headers con autenticación Cognito
  const getAuthHeaders = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      
      if (!idToken) {
        throw new Error('No se pudo obtener el ID token');
      }
      
      return {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      };
    } catch (error) {
      console.error('Error al obtener el token de autenticación:', error);
      throw error;
    }
  };

  // Función para obtener todas las categorías
  const cargarCategorias = async () => {
    setCargandoCategorias(true);
    setErrorCategorias(false);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/categories',
        { headers }
      );

      // Transformar array simple a objetos con id y name
      const categoriasTransformadas = (response.data || []).map(categoria => ({
        id: categoria.toLowerCase().replace(/\s+/g, '-'),
        name: categoria
      }));

      setCategorias(categoriasTransformadas);
      setErrorCategorias(false);
    } catch (error) {
      console.error('Error al cargar categorías:', error);
      setCategorias([]);
      setErrorCategorias(true);
    } finally {
      setCargandoCategorias(false);
    }
  };

  // Funciones para manejar colecciones
  const cargarColecciones = async () => {
    setCargandoColecciones(true);
    setErrorColecciones(false);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/question_collections',
        {
          headers,
          params: {
            mode: 'collections'
          }
        }
      );

      if (response.status === 200) {
        setColecciones(response.data.collections || []);
        setErrorColecciones(false);
      }
    } catch (error) {
      console.error('Error al cargar colecciones:', error);
      setColecciones([]);
      setErrorColecciones(true);
    } finally {
      setCargandoColecciones(false);
    }
  };

  const cargarColeccion = async (collectionId) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/question_collections',
        {
          headers,
          params: {
            mode: 'one',
            collection_id: collectionId
          }
        }
      );

      if (response.status === 200) {
        const coleccion = response.data;
        console.log('Colección cargada:', coleccion);
        console.log('Preguntas con imágenes:', coleccion.questions?.map(q => ({
          id: q.id,
          titulo: q.titulo,
          imagenes: q.imagenes
        })));

        setColeccionActiva(coleccion);
        setTituloGeneral(coleccion.title || '');
        setDescripcionGeneral(coleccion.description || '');
        setCategoriaSeleccionada(coleccion.category || '');
        setConfiguracionGradificacion(coleccion.grading_config || configuracionGradificacion);

        // Procesar preguntas para asegurar que todas las respuestas tengan button_color
        const preguntasIniciales = (coleccion.questions || preguntas).map(pregunta => ({
          ...pregunta,
          respuestas: pregunta.respuestas.map(respuesta => ({
            ...respuesta,
            button_color: respuesta.button_color || '#E5E7EB' // Color por defecto si no existe
          })),
          imagenes: pregunta.imagenes || []
        }));

        // Cargar las imágenes desde el servidor
        const preguntasConImagenes = await cargarImagenesColeccion(collectionId, preguntasIniciales);

        setPreguntas(preguntasConImagenes);
        setModoEdicion('editando');
        setVistaActiva('preguntas');
      }
    } catch (error) {
      console.error('Error al cargar colección:', error);
      alert('Error al cargar la colección seleccionada');
    }
  };

  // Función para cargar imágenes de una colección
  const cargarImagenesColeccion = async (collectionId, preguntas) => {
    try {
      console.log('🖼️ DEBUG - cargarImagenesColeccion iniciada');
      console.log('🖼️ DEBUG - Collection ID:', collectionId);
      console.log('🖼️ DEBUG - Preguntas recibidas:', preguntas);

      // Construir array de archivos a partir de las preguntas
      const files = [];
      preguntas.forEach(pregunta => {
        console.log(`🖼️ DEBUG - Procesando pregunta ${pregunta.id}, imágenes:`, pregunta.imagenes);
        if (pregunta.imagenes && pregunta.imagenes.length > 0) {
          pregunta.imagenes.forEach(imagen => {
            console.log(`🖼️ DEBUG - Procesando imagen:`, imagen);
            // Solo agregar si tiene filename (viene del servidor)
            if (imagen.filename) {
              files.push({
                question_id: pregunta.id,
                filename: imagen.filename
              });
              console.log(`🖼️ DEBUG - Agregada imagen: ${imagen.filename} para pregunta ${pregunta.id}`);
            } else {
              console.log(`🖼️ DEBUG - Imagen sin filename, saltando:`, imagen);
            }
          });
        }
      });

      console.log('🖼️ DEBUG - Array files construido:', files);

      // Si no hay archivos para descargar, retornar las preguntas tal como están
      if (files.length === 0) {
        console.log('🖼️ DEBUG - No hay archivos para descargar, retornando preguntas sin modificar');
        return preguntas;
      }

      console.log('🖼️ DEBUG - Solicitando URLs presigned para archivos:', files);

      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/question_collections',
        {
          headers,
          params: {
            mode: 'images',
            op: 'download',
            collection_id: collectionId,
            files: JSON.stringify(files)
          }
        }
      );

      if (response.status === 200 && response.data.files) {
        console.log('🖼️ DEBUG - URLs presigned recibidas:', response.data.files);

        // Mapear las URLs presigned a las preguntas
        const preguntasConImagenes = preguntas.map(pregunta => {
          console.log(`🖼️ DEBUG - Mapeando pregunta ${pregunta.id}...`);
          const imagenesActualizadas = pregunta.imagenes.map(imagen => {
            console.log(`🖼️ DEBUG - Buscando URL para imagen:`, imagen);
            // Buscar la URL presigned para esta imagen
            const imagenPresigned = response.data.files.find(file =>
              file.question_id === pregunta.id && file.filename === imagen.filename
            );

            console.log(`🖼️ DEBUG - Imagen presigned encontrada:`, imagenPresigned);

            if (imagenPresigned && imagenPresigned.get_url) {
              const imagenActualizada = {
                ...imagen,
                url: imagenPresigned.get_url,
                isFromServer: true,
                key: imagenPresigned.key
              };
              console.log(`🖼️ DEBUG - Imagen actualizada con URL:`, imagenActualizada);
              return imagenActualizada;
            }

            console.log(`🖼️ DEBUG - No se encontró URL presigned para imagen:`, imagen);
            return imagen;
          });

          const preguntaActualizada = {
            ...pregunta,
            imagenes: imagenesActualizadas
          };
          console.log(`🖼️ DEBUG - Pregunta ${pregunta.id} actualizada:`, preguntaActualizada);
          return preguntaActualizada;
        });

        console.log('🖼️ DEBUG - Todas las preguntas con imágenes actualizadas:', preguntasConImagenes);
        return preguntasConImagenes;
      }

      return preguntas;
    } catch (error) {
      console.error('Error al cargar imágenes:', error);
      return preguntas; // Retornar preguntas sin imágenes si hay error
    }
  };

  // Función para subir imágenes al servidor
  const subirImagenesColeccion = async (collectionId, preguntas) => {
    try {
      // Encontrar todas las imágenes nuevas (que tienen file pero no isFromServer)
      const imagenesNuevas = [];

      preguntas.forEach(pregunta => {
        if (pregunta.imagenes && pregunta.imagenes.length > 0) {
          pregunta.imagenes.forEach(imagen => {
            // Solo procesar imágenes nuevas (que tienen archivo file y no vienen del servidor)
            if (imagen.file && !imagen.isFromServer) {
              imagenesNuevas.push({
                question_id: pregunta.id,
                filename: imagen.file.name,
                content_type: imagen.file.type,
                file: imagen.file,
                imagenOriginal: imagen
              });
            }
          });
        }
      });

      if (imagenesNuevas.length === 0) {
        console.log('No hay imágenes nuevas para subir');
        return preguntas; // No hay imágenes nuevas para subir
      }

      console.log('🔍 DEBUG - Subiendo imágenes nuevas:', imagenesNuevas);

      // Solicitar URLs presigned para subida
      const files = imagenesNuevas.map(img => ({
        question_id: img.question_id,
        filename: img.filename,
        content_type: img.content_type
      }));

      console.log('🔍 DEBUG - Array files para solicitar URLs:', files);
      console.log('🔍 DEBUG - Collection ID:', collectionId);

      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/question_collections',
        {
          headers,
          params: {
            mode: 'images',
            op: 'upload',
            collection_id: collectionId,
            files: JSON.stringify(files)
          }
        }
      );

      if (response.status !== 200 || !response.data.files) {
        throw new Error('No se pudieron obtener URLs de subida');
      }

      console.log('URLs presigned para subida recibidas:', response.data.files);

      // Subir cada imagen a S3
      const promesasSubida = imagenesNuevas.map(async (imagenNueva) => {
        const urlInfo = response.data.files.find(file =>
          file.question_id === imagenNueva.question_id && file.filename === imagenNueva.filename
        );

        if (!urlInfo || !urlInfo.put_url) {
          throw new Error(`No se encontró URL de subida para ${imagenNueva.filename}`);
        }

        console.log(`🔍 DEBUG - Subiendo archivo: ${imagenNueva.filename}`);
        console.log(`🔍 DEBUG - Content-Type: ${imagenNueva.content_type}`);
        console.log(`🔍 DEBUG - Tamaño archivo: ${imagenNueva.file.size} bytes`);
        console.log(`🔍 DEBUG - URL presigned: ${urlInfo.put_url}`);
        console.log(`🔍 DEBUG - Archivo object:`, imagenNueva.file);

        // Subir archivo a S3 (SIN Authorization header - las URLs presigned ya incluyen auth)
        const uploadResponse = await fetch(urlInfo.put_url, {
          method: 'PUT',
          body: imagenNueva.file,
          headers: {
            'Content-Type': imagenNueva.content_type
            // NO enviar Authorization - las URLs presigned ya tienen auth
          }
        });

        console.log(`🔍 DEBUG - Response status: ${uploadResponse.status}`);
        console.log(`🔍 DEBUG - Response statusText: ${uploadResponse.statusText}`);

        if (!uploadResponse.ok) {
          const responseText = await uploadResponse.text();
          console.log(`🔍 DEBUG - Response body: ${responseText}`);
          console.log(`🔍 DEBUG - Response headers:`, [...uploadResponse.headers.entries()]);
        }

        if (!uploadResponse.ok) {
          console.error(`❌ Error al subir ${imagenNueva.filename}: ${uploadResponse.status} - ${uploadResponse.statusText}`);
          // No lanzar error si es solo un problema de CORS pero la imagen se subió
          if (uploadResponse.status !== 0) { // 0 puede ser CORS pero subida exitosa
            throw new Error(`Error al subir ${imagenNueva.filename}: ${uploadResponse.status}`);
          }
        }

        console.log(`Imagen subida exitosamente: ${imagenNueva.filename}`);

        return {
          ...imagenNueva,
          key: urlInfo.key,
          uploaded: true
        };
      });

      const imagenesSubidas = await Promise.all(promesasSubida);
      console.log('Todas las imágenes subidas:', imagenesSubidas);

      // Actualizar las preguntas con las imágenes subidas
      const preguntasActualizadas = preguntas.map(pregunta => {
        const imagenesActualizadas = pregunta.imagenes.map(imagen => {
          // Buscar si esta imagen fue subida
          const imagenSubida = imagenesSubidas.find(subida =>
            subida.question_id === pregunta.id &&
            subida.imagenOriginal.id === imagen.id
          );

          if (imagenSubida) {
            return {
              ...imagen,
              filename: imagenSubida.filename,
              key: imagenSubida.key,
              isFromServer: true,
              // Remover el file object ya que ahora está en el servidor
              file: undefined,
              url: undefined // Se eliminará la URL blob temporal
            };
          }

          return imagen;
        });

        return {
          ...pregunta,
          imagenes: imagenesActualizadas
        };
      });

      return preguntasActualizadas;

    } catch (error) {
      console.error('Error al subir imágenes:', error);
      throw error; // Re-lanzar el error para que guardarColeccion lo maneje
    }
  };

  // Función para abrir modal de nueva colección
  const abrirModalNuevaColeccion = () => {
    setDatosNuevaColeccion({
      nombre: '',
      descripcion: '',
      categoria: ''
    });
    setErroresModal({});
    setMostrarModalNuevaColeccion(true);
  };

  // Función para validar datos del modal
  const validarDatosModal = () => {
    const errores = {};

    if (!datosNuevaColeccion.nombre.trim()) {
      errores.nombre = 'El nombre es obligatorio';
    }

    if (!datosNuevaColeccion.categoria) {
      errores.categoria = 'Debes seleccionar una categoría';
    }

    setErroresModal(errores);
    return Object.keys(errores).length === 0;
  };

  // Función para crear nueva colección desde el modal
  const crearNuevaColeccionDesdeModal = () => {
    if (!validarDatosModal()) {
      return;
    }

    setColeccionActiva(null);
    setTituloGeneral(datosNuevaColeccion.nombre);
    setDescripcionGeneral(datosNuevaColeccion.descripcion);
    setCategoriaSeleccionada(datosNuevaColeccion.categoria);
    setPreguntas([{
      id: 1,
      titulo: '',
      pregunta: '',
      ejemplos: '',
      imagenes: [],
      respuestas: [
        { id: Date.now(), texto: '', puntos: 0, gradoDirecto: '', accionSiguiente: 'solo_puntos', button_color: '#E5E7EB' }
      ]
    }]);
    setConfiguracionGradificacion({
      tipoGradificacion: 'puntajes',
      grados: [
        { id: 'A', nombre: 'Grado A', descripcion: 'Excelente estado', color: '#10B981' },
        { id: 'B', nombre: 'Grado B', descripcion: 'Buen estado', color: '#F59E0B' },
        { id: 'C', nombre: 'Grado C', descripcion: 'Estado regular', color: '#EF4444' }
      ],
      thresholds: [
        { gradoId: 'A', min: 0, max: 5 },
        { gradoId: 'B', min: 6, max: 15 },
        { gradoId: 'C', min: 16, max: 999 }
      ]
    });
    setPreguntaActiva(0);
    setModoEdicion('nuevo');
    setMostrarModalNuevaColeccion(false);
    setVistaActiva('preguntas');
  };

  const crearNuevaColeccion = () => {
    abrirModalNuevaColeccion();
  };

  const eliminarColeccion = async (collectionId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta colección? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      const headers = await getAuthHeaders();
      await axios.delete(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/question_collections',
        {
          headers,
          params: {
            collection_id: collectionId,
            purge: 1
          }
        }
      );
      
      // Recargar colecciones
      await cargarColecciones();
      
      // Si era la colección activa, limpiar
      if (coleccionActiva?.collection_id === collectionId) {
        crearNuevaColeccion();
      }
      
      alert('Colección eliminada exitosamente');
    } catch (error) {
      console.error('Error al eliminar colección:', error);
      alert('Error al eliminar la colección');
    }
  };

  // Función para agregar una nueva pregunta
  const agregarPregunta = () => {
    const nuevaPregunta = {
      id: Date.now(),
      titulo: '',
      pregunta: '',
      ejemplos: '',
      imagenes: [],
      respuestas: [
        { id: Date.now(), texto: '', puntos: 0, gradoDirecto: '', accionSiguiente: 'solo_puntos', button_color: '#E5E7EB' }
      ]
    };
    setPreguntas([...preguntas, nuevaPregunta]);
    setPreguntaActiva(preguntas.length);
  };

  // Función para eliminar una pregunta
  const eliminarPregunta = (indice) => {
    if (preguntas.length <= 1) {
      alert('Debe existir al menos una pregunta en la colección.');
      return;
    }
    
    const nuevasPreguntas = preguntas.filter((_, i) => i !== indice);
    setPreguntas(nuevasPreguntas);
    
    // Ajustar la pregunta activa si es necesario
    if (preguntaActiva >= nuevasPreguntas.length) {
      setPreguntaActiva(nuevasPreguntas.length - 1);
    }
  };

  // Función para actualizar una pregunta
  const actualizarPregunta = (campo, valor) => {
    const nuevasPreguntas = [...preguntas];
    nuevasPreguntas[preguntaActiva][campo] = valor;
    setPreguntas(nuevasPreguntas);
    
    // Limpiar errores del campo actualizado
    if (errores[`pregunta_${preguntaActiva}_${campo}`]) {
      const nuevosErrores = { ...errores };
      delete nuevosErrores[`pregunta_${preguntaActiva}_${campo}`];
      setErrores(nuevosErrores);
    }
  };

  // Función para manejar la selección de archivos
  const manejarSeleccionArchivos = (event) => {
    const archivos = Array.from(event.target.files);
    const archivosValidos = [];
    const erroresArchivos = [];

    archivos.forEach((archivo, index) => {
      if (!archivo.type.startsWith('image/')) {
        erroresArchivos.push(`${archivo.name}: Solo se permiten archivos de imagen`);
        return;
      }

      if (archivo.size > 5 * 1024 * 1024) { // 5MB límite
        erroresArchivos.push(`${archivo.name}: El archivo es muy grande (máximo 5MB)`);
        return;
      }

      // Generar ID único usando timestamp + contador secuencial
      const nuevoContador = contadorImagenes + index + 1;

      archivosValidos.push({
        file: archivo,
        url: URL.createObjectURL(archivo),
        id: Date.now() + nuevoContador, // ID entero único
        filename: archivo.name
      });
    });

    if (erroresArchivos.length > 0) {
      alert('Algunos archivos no pudieron ser procesados:\n' + erroresArchivos.join('\n'));
    }

    const preguntaActual = preguntas[preguntaActiva];
    const imagenesTotales = preguntaActual.imagenes.length + archivosValidos.length;
    
    if (imagenesTotales > 6) {
      alert('No puedes agregar más de 6 imágenes por pregunta');
      return;
    }

    // Actualizar contador de imágenes
    setContadorImagenes(prev => prev + archivosValidos.length);

    actualizarPregunta('imagenes', [...preguntaActual.imagenes, ...archivosValidos]);

    // Limpiar el input
    event.target.value = '';
  };

  // Función para eliminar una imagen
  const eliminarImagen = (imagenId) => {
    const preguntaActual = preguntas[preguntaActiva];
    const nuevasImagenes = preguntaActual.imagenes.filter(img => img.id !== imagenId);
    
    // Liberar la URL del objeto para evitar memory leaks
    const imagenEliminada = preguntaActual.imagenes.find(img => img.id === imagenId);
    if (imagenEliminada && imagenEliminada.url) {
      URL.revokeObjectURL(imagenEliminada.url);
    }
    
    actualizarPregunta('imagenes', nuevasImagenes);
  };

  // Función para validar el formulario
  const validarFormulario = () => {
    const nuevosErrores = {};

    // Validar título general
    if (!tituloGeneral.trim()) {
      nuevosErrores.tituloGeneral = 'El título general es obligatorio';
    }

    // Validar categoría
    if (!categoriaSeleccionada) {
      nuevosErrores.categoria = 'Debes seleccionar una categoría';
    }

    // Validar cada pregunta
    preguntas.forEach((pregunta, indice) => {
      if (!pregunta.titulo.trim()) {
        nuevosErrores[`pregunta_${indice}_titulo`] = 'El título de la pregunta es obligatorio';
      }
      if (!pregunta.pregunta.trim()) {
        nuevosErrores[`pregunta_${indice}_pregunta`] = 'El texto de la pregunta es obligatorio';
      }
    });

    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  // Función para guardar la colección
  const guardarColeccion = async () => {
    if (!validarFormulario()) {
      alert('Por favor, completa todos los campos obligatorios antes de guardar.');
      return;
    }

    setGuardando(true);

    try {
      const headers = await getAuthHeaders();

      // 1. Subir imágenes primero (si hay nuevas)
      let preguntasConImagenesSubidas = preguntas;

      // Para nuevas colecciones, generar un ID temporal para subir las imágenes
      const collectionIdParaImagenes = coleccionActiva?.collection_id || `temp_${Date.now()}`;

      try {
        console.log('Subiendo imágenes antes de guardar colección...');
        preguntasConImagenesSubidas = await subirImagenesColeccion(collectionIdParaImagenes, preguntas);
        console.log('Imágenes subidas exitosamente');
      } catch (errorImagenes) {
        console.error('Error al subir imágenes:', errorImagenes);
        alert('Error al subir las imágenes. Por favor, inténtalo de nuevo.');
        setGuardando(false);
        return;
      }

      // 2. Limpiar las preguntas para el servidor (remover propiedades temporales)
      const preguntasParaServidor = preguntasConImagenesSubidas.map(pregunta => ({
        ...pregunta,
        imagenes: pregunta.imagenes.map(imagen => {
          // Solo mantener propiedades que el servidor necesita
          const imagenLimpia = {
            id: imagen.id,
            filename: imagen.filename,
            key: imagen.key
          };
          // Solo agregar propiedades que existen
          if (imagen.name) imagenLimpia.name = imagen.name;
          if (imagen.alt) imagenLimpia.alt = imagen.alt;
          return imagenLimpia;
        }).filter(imagen => imagen.filename) // Solo mantener imágenes que tienen filename (están en el servidor)
      }));

      // 3. Preparar datos de la colección con imágenes ya subidas
      const coleccionData = {
        title: tituloGeneral,
        description: descripcionGeneral,
        category: categoriaSeleccionada,
        grading_config: configuracionGradificacion,
        questions: preguntasParaServidor,
        total_questions: preguntasParaServidor.length
      };

      // Si estamos editando, incluir el collection_id en el body
      if (modoEdicion === 'editando' && coleccionActiva?.collection_id) {
        coleccionData.collection_id = coleccionActiva.collection_id;
      }

      console.log('🔄 DEBUG - Enviando colección:', {
        modo: modoEdicion,
        collection_id: coleccionActiva?.collection_id,
        url: 'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/question_collections',
        data: coleccionData
      });

      let response;
      if (modoEdicion === 'editando' && coleccionActiva?.collection_id) {
        // Opción alternativa: usar PUT con query parameter en lugar de path parameter
        response = await axios.put(
          'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/question_collections',
          coleccionData,
          {
            headers,
            params: {
              collection_id: coleccionActiva.collection_id
            }
          }
        );
      } else {
        // Crear nueva colección
        response = await axios.post(
          'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/question_collections',
          coleccionData,
          { headers }
        );
      }

      if (response.status === 200 || response.status === 201) {
        setMostrarConfirmacion(true);
        setTimeout(() => setMostrarConfirmacion(false), 3000);
        
        // Recargar colecciones
        await cargarColecciones();
        
        // Actualizar el estado con las preguntas que incluyen las imágenes subidas
        setPreguntas(preguntasConImagenesSubidas);

        // Si era una nueva colección, actualizarla como activa
        if (modoEdicion === 'nuevo' && response.data?.collection_id) {
          setColeccionActiva({
            ...response.data,
            ...coleccionData
          });
          setModoEdicion('editando');
        }
      }
    } catch (error) {
      console.error('Error al guardar colección:', error);
      alert('Error al guardar la colección. Por favor, inténtalo de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  // Función para cancelar y limpiar
  const cancelar = () => {
    if (window.confirm('¿Estás seguro de que quieres cancelar? Se perderán todos los cambios no guardados.')) {
      setTituloGeneral('');
      setPreguntas([{
        id: 1,
        titulo: '',
        pregunta: '',
        ejemplos: '',
        imagenes: []
      }]);
      setPreguntaActiva(0);
      setErrores({});
    }
  };

  // Función para abrir vista previa
  const abrirVistaPrevia = () => {
    if (!validarFormulario()) {
      alert('Por favor, completa todos los campos obligatorios antes de ver la vista previa.');
      return;
    }
    setMostrarSimulador(true);
  };

  // Función para duplicar una pregunta
  const duplicarPregunta = (indice) => {
    const preguntaOriginal = preguntas[indice];
    const timestamp = Date.now();

    const preguntaDuplicada = {
      ...preguntaOriginal,
      id: timestamp,
      titulo: `${preguntaOriginal.titulo} (Copia)`,
      imagenes: preguntaOriginal.imagenes.map((img, imgIndex) => {
        const nuevoContador = contadorImagenes + imgIndex + 1;
        return {
          ...img,
          id: timestamp + nuevoContador, // ID entero único
          url: img.url
        };
      }),
      respuestas: preguntaOriginal.respuestas.map((resp, respIndex) => ({
        ...resp,
        id: timestamp + respIndex + 1000 // ID entero único con offset
      }))
    };

    // Actualizar contador de imágenes
    setContadorImagenes(prev => prev + preguntaOriginal.imagenes.length);

    const nuevasPreguntas = [...preguntas];
    nuevasPreguntas.splice(indice + 1, 0, preguntaDuplicada);
    setPreguntas(nuevasPreguntas);
  };

  // Funciones para manejar respuestas
  const agregarRespuesta = () => {
    const nuevaRespuesta = {
      id: Date.now(),
      texto: '',
      puntos: 0,
      gradoDirecto: '',
      accionSiguiente: 'solo_puntos',
      button_color: '#E5E7EB'
    };
    const nuevasPreguntas = [...preguntas];
    nuevasPreguntas[preguntaActiva].respuestas.push(nuevaRespuesta);
    setPreguntas(nuevasPreguntas);
  };

  const eliminarRespuesta = (respuestaId) => {
    const preguntaActual = preguntas[preguntaActiva];
    if (preguntaActual.respuestas.length <= 1) {
      alert('Debe existir al menos una respuesta por pregunta.');
      return;
    }
    
    const nuevasPreguntas = [...preguntas];
    nuevasPreguntas[preguntaActiva].respuestas = preguntaActual.respuestas.filter(r => r.id !== respuestaId);
    setPreguntas(nuevasPreguntas);
  };

  // Función helper para obtener información de color
  const obtenerInfoColor = (colorHex) => {
    const opcionEncontrada = opcionesColores.find(opcion => opcion.color === colorHex);
    return opcionEncontrada || opcionesColores.find(opcion => opcion.id === 'personalizado');
  };

  const actualizarRespuesta = (respuestaId, campo, valor) => {
    const nuevasPreguntas = [...preguntas];
    const respuestaIndex = nuevasPreguntas[preguntaActiva].respuestas.findIndex(r => r.id === respuestaId);
    if (respuestaIndex !== -1) {
      nuevasPreguntas[preguntaActiva].respuestas[respuestaIndex][campo] = valor;
      setPreguntas(nuevasPreguntas);
    }
  };

  // Funciones para configuración de gradificación
  const actualizarConfiguracion = (campo, valor) => {
    setConfiguracionGradificacion(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const agregarGrado = () => {
    const nuevoGrado = {
      id: String.fromCharCode(65 + configuracionGradificacion.grados.length),
      nombre: `Grado ${String.fromCharCode(65 + configuracionGradificacion.grados.length)}`,
      descripcion: '',
      color: '#6366F1'
    };
    
    const nuevosGrados = [...configuracionGradificacion.grados, nuevoGrado];
    const nuevoThreshold = {
      gradoId: nuevoGrado.id,
      min: 0,
      max: 999
    };
    
    setConfiguracionGradificacion(prev => ({
      ...prev,
      grados: nuevosGrados,
      thresholds: [...prev.thresholds, nuevoThreshold]
    }));
  };

  const eliminarGrado = (gradoId) => {
    if (configuracionGradificacion.grados.length <= 1) {
      alert('Debe existir al menos un grado de evaluación.');
      return;
    }
    
    setConfiguracionGradificacion(prev => ({
      ...prev,
      grados: prev.grados.filter(g => g.id !== gradoId),
      thresholds: prev.thresholds.filter(t => t.gradoId !== gradoId)
    }));
  };

  const actualizarGrado = (gradoId, campo, valor) => {
    setConfiguracionGradificacion(prev => ({
      ...prev,
      grados: prev.grados.map(g => 
        g.id === gradoId ? { ...g, [campo]: valor } : g
      )
    }));
  };

  const actualizarThreshold = (gradoId, campo, valor) => {
    setConfiguracionGradificacion(prev => ({
      ...prev,
      thresholds: prev.thresholds.map(t => 
        t.gradoId === gradoId ? { ...t, [campo]: parseInt(valor) || 0 } : t
      )
    }));
  };

  const preguntaActual = preguntas[preguntaActiva];

  return (
    <div className="gestion-preguntas-container">
      {/* Header estilo Campaigns */}
      <header className="header">
        <Logo 
          variant="horizontal" 
          size="medium" 
          onClick={() => navigate('/dashboard')} 
        />
        <div className="header-title">Gestión de Preguntas</div>
        <div className="user-menu">
          <button className="btn btn-outline" onClick={() => navigate('/dashboard')}>
            Volver al Dashboard
          </button>
        </div>
      </header>

      <div className="container">
        {/* Navegación de pestañas */}
        <div className="tabs-navigation">
          <button 
            className={`tab-button ${vistaActiva === 'colecciones' ? 'active' : ''}`}
            onClick={() => setVistaActiva('colecciones')}
          >
            📂 Mis Colecciones
          </button>
          <button
            className={`tab-button ${vistaActiva === 'preguntas' ? 'active' : ''} ${!tituloGeneral ? 'disabled' : ''}`}
            onClick={() => tituloGeneral && setVistaActiva('preguntas')}
            disabled={!tituloGeneral}
          >
            📝 Preguntas y Respuestas
          </button>
          <button
            className={`tab-button ${vistaActiva === 'configuracion' ? 'active' : ''} ${!tituloGeneral ? 'disabled' : ''}`}
            onClick={() => tituloGeneral && setVistaActiva('configuracion')}
            disabled={!tituloGeneral}
          >
            ⚙️ Configuración de Gradificación
          </button>
        </div>

        {vistaActiva === 'colecciones' ? (
          /* Vista de Gestión de Colecciones */
          <div className="collections-view">
            <div className="collections-header">
              <div className="collections-title">
                <h2>📂 Gestión de Colecciones de Preguntas</h2>
                <p>Administra tus formularios de evaluación. Crea nuevas colecciones o edita las existentes.</p>
              </div>
              <button
                className="btn btn-primary"
                onClick={abrirModalNuevaColeccion}
              >
                <span className="btn-icon">➕</span>
                Nueva Colección
              </button>
            </div>

            {cargandoColecciones ? (
              <div className="loading-collections">
                <div className="loading-spinner"></div>
                <p>Cargando colecciones...</p>
              </div>
            ) : (
              <div className="collections-grid">
                {colecciones.length === 0 ? (
                  errorColecciones ? (
                    <div className="empty-collections">
                      <div className="empty-icon">⚠️</div>
                      <h3>Error al cargar colecciones</h3>
                      <p>No se pudieron cargar tus colecciones. Verifica tu conexión a internet e inténtalo de nuevo.</p>
                      <button
                        className="btn btn-primary"
                        onClick={cargarColecciones}
                      >
                        <span className="btn-icon">🔄</span>
                        Reintentar
                      </button>
                    </div>
                  ) : (
                    <div className="empty-collections">
                      <div className="empty-icon">📝</div>
                      <h3>No tienes colecciones creadas</h3>
                      <p>Comienza creando tu primera colección de preguntas para evaluaciones</p>
                      <button
                        className="btn btn-primary"
                        onClick={abrirModalNuevaColeccion}
                      >
                        <span className="btn-icon">➕</span>
                        Crear Primera Colección
                      </button>
                    </div>
                  )
                ) : (
                  colecciones.map((coleccion) => (
                    <div key={coleccion.collection_id} className="collection-card">
                      <div className="collection-header">
                        <div className="collection-category">
                          <span className="category-badge">
                            📱 {categorias.find(cat => cat.id === coleccion.category)?.name || coleccion.category || 'Sin categoría'}
                          </span>
                        </div>
                        <div className="collection-actions">
                          <button
                            className="btn-icon-small"
                            onClick={() => cargarColeccion(coleccion.collection_id)}
                            title="Editar colección"
                          >
                            ✏️
                          </button>
                          <button
                            className="btn-icon-small danger"
                            onClick={() => eliminarColeccion(coleccion.collection_id)}
                            title="Eliminar colección"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                      
                      <div className="collection-content">
                        <h3 
                          className="collection-title"
                          onClick={() => cargarColeccion(coleccion.collection_id)}
                        >
                          {coleccion.title}
                        </h3>
                        <p className="collection-description">
                          {coleccion.description || 'Sin descripción'}
                        </p>
                        
                        <div className="collection-stats">
                          <div className="stat">
                            <span className="stat-icon">❓</span>
                            <span className="stat-value">{coleccion.total_questions}</span>
                            <span className="stat-label">preguntas</span>
                          </div>
                          <div className="stat stat-date">
                            <span className="stat-icon">📅</span>
                            <div className="stat-date-content">
                              <span className="stat-value">
                                {new Date(coleccion.updated_at).toLocaleDateString()}
                              </span>
                              <span className="stat-label">actualizado</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="collection-footer">
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => {/* Implementar simulación */}}
                        >
                          <span className="btn-icon">🎯</span>
                          Simular
                        </button>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => cargarColeccion(coleccion.collection_id)}
                        >
                          <span className="btn-icon">✏️</span>
                          Editar
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        ) : vistaActiva === 'preguntas' ? (
          <>
            {/* Barra de progreso */}
            <div className="progress-section">
              <div className="progress-info">
                <div className="progress-main">
                  <span>Pregunta {preguntaActiva + 1} de {preguntas.length}</span>
                  <span className="questions-count">{preguntas.length} pregunta{preguntas.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="progress-status">
                  <div className="status-item">
                    <span className="status-label">Estado:</span>
                    <span className="status-value">Borrador</span>
                  </div>
                  <div className="status-item">
                    <span className="status-label">Modificado:</span>
                    <span className="status-value">Hace unos momentos</span>
                  </div>
                </div>
              </div>
              <div className="progress-line">
                <div 
                  className="progress-fill"
                  style={{ width: `${((preguntaActiva + 1) / preguntas.length) * 100}%` }}
                ></div>
              </div>
            </div>

      <div className="gestion-content">
        {/* Panel izquierdo - Lista de preguntas */}
        <div className="questions-sidebar">
          <div className="sidebar-header">
            <h3>Colección de Preguntas</h3>
            <button 
              className="btn btn-primary btn-sm"
              onClick={agregarPregunta}
              title="Agregar nueva pregunta"
            >
              <span className="btn-icon">➕</span>
              Nueva
            </button>
          </div>

          {/* Título general y descripción */}
          <div className="general-title-section">
            <div className="form-group">
              <label className="form-label">
                Título General de la Colección <span className="required">*</span>
              </label>
              <input
                type="text"
                className={`form-input ${errores.tituloGeneral ? 'error' : ''}`}
                value={tituloGeneral}
                onChange={(e) => {
                  setTituloGeneral(e.target.value);
                  if (errores.tituloGeneral) {
                    const nuevosErrores = { ...errores };
                    delete nuevosErrores.tituloGeneral;
                    setErrores(nuevosErrores);
                  }
                }}
                placeholder="Ej: Evaluación de Estado del Dispositivo"
                maxLength={100}
              />
              {errores.tituloGeneral && (
                <div className="error-message">{errores.tituloGeneral}</div>
              )}
            </div>
            
            <div className="form-group">
              <label className="form-label">
                Descripción de la Colección
              </label>
              <textarea
                className="form-textarea"
                value={descripcionGeneral}
                onChange={(e) => setDescripcionGeneral(e.target.value)}
                placeholder="Describe el propósito y alcance de esta colección de preguntas..."
                rows={3}
                maxLength={300}
              />
              <div className="char-counter">
                {descripcionGeneral.length}/300 caracteres
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                Categoría <span className="required">*</span>
              </label>
              <select
                className={`form-input ${errores.categoria ? 'error' : ''}`}
                value={categoriaSeleccionada}
                onChange={(e) => {
                  setCategoriaSeleccionada(e.target.value);
                  if (errores.categoria) {
                    const nuevosErrores = { ...errores };
                    delete nuevosErrores.categoria;
                    setErrores(nuevosErrores);
                  }
                }}
                disabled={cargandoCategorias || errorCategorias}
              >
                <option value="">Selecciona una categoría</option>
                {categorias.map(categoria => (
                  <option key={categoria.id} value={categoria.id}>
                    {categoria.name}
                  </option>
                ))}
              </select>
              {errores.categoria && (
                <div className="error-message">{errores.categoria}</div>
              )}
              {cargandoCategorias && (
                <div className="loading-text">Cargando categorías...</div>
              )}
              {!cargandoCategorias && errorCategorias && (
                <div className="error-message">
                  No se pudieron cargar las categorías.
                  <button
                    type="button"
                    style={{marginLeft: '8px', textDecoration: 'underline', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer'}}
                    onClick={cargarCategorias}
                  >
                    Reintentar
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Lista de preguntas */}
          <div className="questions-list">
            {preguntas.map((pregunta, indice) => (
              <div 
                key={pregunta.id}
                className={`question-item ${indice === preguntaActiva ? 'active' : ''}`}
                onClick={() => setPreguntaActiva(indice)}
              >
                <div className="question-item-header">
                  <span className="question-number">{indice + 1}</span>
                  <div className="question-item-content">
                    <div className="question-item-title">
                      {pregunta.titulo || `Pregunta ${indice + 1}`}
                    </div>
                    <div className="question-item-preview">
                      {pregunta.pregunta ? 
                        pregunta.pregunta.substring(0, 50) + (pregunta.pregunta.length > 50 ? '...' : '') 
                        : 'Sin contenido'
                      }
                    </div>
                    {pregunta.imagenes.length > 0 && (
                      <div className="question-item-images">
                        📷 {pregunta.imagenes.length} imagen{pregunta.imagenes.length !== 1 ? 'es' : ''}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="question-item-actions">
                  <button 
                    className="btn-icon-small"
                    onClick={(e) => {
                      e.stopPropagation();
                      duplicarPregunta(indice);
                    }}
                    title="Duplicar pregunta"
                  >
                    📋
                  </button>
                  {preguntas.length > 1 && (
                    <button 
                      className="btn-icon-small danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        eliminarPregunta(indice);
                      }}
                      title="Eliminar pregunta"
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Botones principales de la colección */}
          <div className="collection-actions">
            <button 
              className="btn btn-secondary btn-collection"
              onClick={abrirVistaPrevia}
              disabled={guardando}
            >
              <span className="btn-icon">👁️</span>
              Simular preguntas
            </button>
            
            <button 
              className={`btn btn-primary btn-collection ${guardando ? 'loading' : ''}`}
              onClick={guardarColeccion}
              disabled={guardando}
            >
              <span className="btn-icon">{guardando ? '⏳' : '💾'}</span>
              {guardando ? 'Guardando...' : 'Guardar Colección'}
            </button>
          </div>
        </div>

        {/* Panel derecho - Editor de pregunta */}
        <div className="question-editor">
          <div className="editor-header">
            <h3>Editando Pregunta {preguntaActiva + 1}</h3>
            <div className="editor-nav">
              <button 
                className="btn btn-outline btn-sm"
                onClick={() => setPreguntaActiva(Math.max(0, preguntaActiva - 1))}
                disabled={preguntaActiva === 0}
              >
                ← Anterior
              </button>
              <button 
                className="btn btn-outline btn-sm"
                onClick={() => setPreguntaActiva(Math.min(preguntas.length - 1, preguntaActiva + 1))}
                disabled={preguntaActiva === preguntas.length - 1}
              >
                Siguiente →
              </button>
            </div>
          </div>

          <div className="editor-content">
            {/* Título de la pregunta */}
            <div className="form-group">
              <label className="form-label">
                Título de la Pregunta <span className="required">*</span>
              </label>
              <input
                type="text"
                className={`form-input ${errores[`pregunta_${preguntaActiva}_titulo`] ? 'error' : ''}`}
                value={preguntaActual.titulo}
                onChange={(e) => actualizarPregunta('titulo', e.target.value)}
                placeholder="Ej: Estado de la pantalla"
                maxLength={80}
              />
              {errores[`pregunta_${preguntaActiva}_titulo`] && (
                <div className="error-message">{errores[`pregunta_${preguntaActiva}_titulo`]}</div>
              )}
            </div>

            {/* Contenido de la pregunta */}
            <div className="form-group">
              <label className="form-label">
                Pregunta <span className="required">*</span>
              </label>
              <textarea
                className={`form-textarea ${errores[`pregunta_${preguntaActiva}_pregunta`] ? 'error' : ''}`}
                value={preguntaActual.pregunta}
                onChange={(e) => actualizarPregunta('pregunta', e.target.value)}
                placeholder="Escribe aquí la pregunta que verán los usuarios..."
                rows={4}
                maxLength={500}
              />
              <div className="char-counter">
                {preguntaActual.pregunta.length}/500 caracteres
              </div>
              {errores[`pregunta_${preguntaActiva}_pregunta`] && (
                <div className="error-message">{errores[`pregunta_${preguntaActiva}_pregunta`]}</div>
              )}
            </div>

            {/* Ejemplos */}
            <div className="form-group">
              <label className="form-label">
                Ejemplos (Opcional)
              </label>
              <textarea
                className="form-textarea"
                value={preguntaActual.ejemplos}
                onChange={(e) => actualizarPregunta('ejemplos', e.target.value)}
                placeholder="Proporciona ejemplos que ayuden a los usuarios a entender mejor la pregunta..."
                rows={3}
                maxLength={300}
              />
              <div className="char-counter">
                {preguntaActual.ejemplos.length}/300 caracteres
              </div>
            </div>

            {/* Imágenes */}
            <div className="form-group">
              <label className="form-label">
                Imágenes de Referencia (Opcional)
              </label>
              <div className="images-section">
                <div className="images-upload">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={manejarSeleccionArchivos}
                    multiple
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={preguntaActual.imagenes.length >= 6}
                  >
                    <span className="btn-icon">📷</span>
                    {preguntaActual.imagenes.length === 0 ? 'Agregar Imágenes' : 'Agregar Más Imágenes'}
                  </button>
                  <div className="upload-info">
                    Máximo 6 imágenes • Hasta 5MB por imagen • JPG, PNG, GIF
                  </div>
                </div>

                {preguntaActual.imagenes.length > 0 && (
                  <div className="images-preview">
                    {preguntaActual.imagenes.map(imagen => (
                      <div key={imagen.id} className="image-preview">
                        {imagen.url ? (
                          <img src={imagen.url} alt="Vista previa" />
                        ) : (
                          <div className="image-unavailable">
                            <div className="unavailable-icon">📷</div>
                            <div className="unavailable-text">Imagen no disponible</div>
                            <div className="unavailable-subtitle">
                              {imagen.isUnavailable ? 'Recarga la imagen para verla' : 'Imagen del servidor no encontrada'}
                            </div>
                          </div>
                        )}
                        <button
                          type="button"
                          className="remove-image-btn"
                          onClick={() => eliminarImagen(imagen.id)}
                          title="Eliminar imagen"
                        >
                          ✕
                        </button>
                        <div className="image-name">
                          {imagen.filename || imagen.file?.name || imagen.name || 'Imagen sin nombre'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Respuestas */}
            <div className="form-group">
              <div className="responses-header">
                <label className="form-label">
                  Opciones de Respuesta <span className="required">*</span>
                </label>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={agregarRespuesta}
                >
                  <span className="btn-icon">➕</span>
                  Agregar Respuesta
                </button>
              </div>
              
              <div className="responses-list">
                {preguntaActual.respuestas.map((respuesta, index) => (
                  <div key={respuesta.id} className="response-item">
                    <div className="response-header">
                      <span className="response-number">{index + 1}</span>
                      <div className="response-actions">
                        {preguntaActual.respuestas.length > 1 && (
                          <button
                            type="button"
                            className="btn-icon-small danger"
                            onClick={() => eliminarRespuesta(respuesta.id)}
                            title="Eliminar respuesta"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <div className="response-content">
                      <div className="form-group-inline">
                        <label className="form-label-small">Texto de la Respuesta</label>
                        <input
                          type="text"
                          className="form-input"
                          value={respuesta.texto}
                          onChange={(e) => actualizarRespuesta(respuesta.id, 'texto', e.target.value)}
                          placeholder="Ej: Excelente estado, sin rayones"
                          maxLength={200}
                        />
                      </div>

                      {/* Selector de Color del Botón */}
                      <div className="form-group-inline">
                        <label className="form-label-small">Color del Botón</label>
                        <div className="color-selector">
                          <div className="color-options">
                            {opcionesColores.map((opcion) => (
                              <button
                                key={opcion.id}
                                type="button"
                                className={`color-option ${(respuesta.button_color === opcion.color ||
                                  (opcion.id === 'personalizado' && !opcionesColores.some(opt => opt.color === respuesta.button_color))) ? 'active' : ''}`}
                                onClick={() => {
                                  if (opcion.id === 'personalizado') {
                                    // Para personalizado, mostrar el color picker
                                    const colorInput = document.createElement('input');
                                    colorInput.type = 'color';
                                    colorInput.value = respuesta.button_color;
                                    colorInput.click();
                                    colorInput.onchange = (e) => {
                                      actualizarRespuesta(respuesta.id, 'button_color', e.target.value);
                                    };
                                  } else {
                                    actualizarRespuesta(respuesta.id, 'button_color', opcion.color);
                                  }
                                }}
                                title={opcion.descripcion}
                                style={{
                                  backgroundColor: opcion.id === 'personalizado' ? respuesta.button_color : opcion.color,
                                  border: `2px solid ${(respuesta.button_color === opcion.color ||
                                    (opcion.id === 'personalizado' && !opcionesColores.some(opt => opt.color === respuesta.button_color))) ? '#6366F1' : '#E5E7EB'}`
                                }}
                              >
                                <span className="color-option-icon">{opcion.icono}</span>
                                <span className="color-option-name">{opcion.nombre}</span>
                              </button>
                            ))}
                          </div>
                          <div className="color-preview" style={{ backgroundColor: respuesta.button_color }}>
                            <span>Vista previa</span>
                          </div>
                        </div>
                      </div>

                      {/* Configuración según el tipo de gradificación */}
                      <div className="response-grading-config">
                        {configuracionGradificacion.tipoGradificacion === 'puntajes' && (
                          <div className="form-group-inline">
                            <label className="form-label-small">Puntos</label>
                            <input
                              type="number"
                              className="form-input form-input-small"
                              value={respuesta.puntos}
                              onChange={(e) => actualizarRespuesta(respuesta.id, 'puntos', parseInt(e.target.value) || 0)}
                              min="0"
                              max="999"
                              placeholder="0"
                            />
                            <div className="input-help">Mayor puntaje = peor estado</div>
                          </div>
                        )}

                        {configuracionGradificacion.tipoGradificacion === 'grados_directos' && (
                          <>
                            <div className="form-group-inline">
                              <label className="form-label-small">Acción</label>
                              <select
                                className="form-input"
                                value={respuesta.accionSiguiente}
                                onChange={(e) => actualizarRespuesta(respuesta.id, 'accionSiguiente', e.target.value)}
                              >
                                <option value="continuar">Continuar a siguiente pregunta</option>
                                <option value="finalizar_con_grado">Finalizar evaluación aquí y asignar grado</option>
                                <option value="no_retomable">Finalizar, dispositivo no retomable</option>
                              </select>
                            </div>

                            {respuesta.accionSiguiente === 'finalizar_con_grado' && (
                              <div className="form-group-inline">
                                <label className="form-label-small">Grado a Asignar</label>
                                <select
                                  className="form-input"
                                  value={respuesta.gradoDirecto}
                                  onChange={(e) => actualizarRespuesta(respuesta.id, 'gradoDirecto', e.target.value)}
                                >
                                  <option value="">Selecciona un grado</option>
                                  <option value="sin_valor">Sin valor - No ofrecer dinero</option>
                                  {configuracionGradificacion.grados.map(grado => (
                                    <option key={grado.id} value={grado.id}>
                                      {grado.nombre}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </>
                        )}

                        {configuracionGradificacion.tipoGradificacion === 'hibrida' && (
                          <>
                            <div className="form-group-inline">
                              <label className="form-label-small">Acción</label>
                              <select
                                className="form-input"
                                value={respuesta.accionSiguiente}
                                onChange={(e) => actualizarRespuesta(respuesta.id, 'accionSiguiente', e.target.value)}
                              >
                                <option value="solo_puntos">Solo sumar puntos</option>
                                <option value="finalizar_con_grado">Finalizar evaluación aquí y asignar grado</option>
                                <option value="no_retomable">Finalizar, dispositivo no retomable</option>
                              </select>
                            </div>

                            {respuesta.accionSiguiente === 'solo_puntos' && (
                              <div className="form-group-inline">
                                <label className="form-label-small">Puntos</label>
                                <input
                                  type="number"
                                  className="form-input form-input-small"
                                  value={respuesta.puntos}
                                  onChange={(e) => actualizarRespuesta(respuesta.id, 'puntos', parseInt(e.target.value) || 0)}
                                  min="0"
                                  max="999"
                                  placeholder="0"
                                />
                                <div className="input-help">Mayor puntaje = peor estado</div>
                              </div>
                            )}

                            {respuesta.accionSiguiente === 'finalizar_con_grado' && (
                              <div className="form-group-inline">
                                <label className="form-label-small">Grado a Asignar</label>
                                <select
                                  className="form-input"
                                  value={respuesta.gradoDirecto}
                                  onChange={(e) => actualizarRespuesta(respuesta.id, 'gradoDirecto', e.target.value)}
                                >
                                  <option value="">Selecciona un grado</option>
                                  <option value="sin_valor">Sin valor - No ofrecer dinero</option>
                                  {configuracionGradificacion.grados.map(grado => (
                                    <option key={grado.id} value={grado.id}>
                                      {grado.nombre}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Solo botón cancelar en el editor */}
            <div className="editor-cancel-section">
              <button 
                className="btn btn-outline"
                onClick={cancelar}
                disabled={guardando}
              >
                <span className="btn-icon">↶</span>
                Cancelar Cambios
              </button>
            </div>
          </div>
        </div>
      </div>
          </>
        ) : (
          /* Vista de Configuración de Gradificación */
          <div className="configuration-view">
            <div className="card">
              <h2 className="card-title">⚙️ Configuración de Gradificación</h2>
              <p className="card-subtitle">
                Define cómo se evaluarán y calificarán las respuestas de los usuarios
              </p>

              {/* Tipo de Gradificación */}
              <div className="form-group">
                <label className="form-label">
                  Tipo de Gradificación <span className="required">*</span>
                </label>
                <div className="grading-types">
                  <div 
                    className={`grading-type-card ${configuracionGradificacion.tipoGradificacion === 'puntajes' ? 'active' : ''}`}
                    onClick={() => actualizarConfiguracion('tipoGradificacion', 'puntajes')}
                  >
                    <div className="grading-type-icon">📊</div>
                    <div className="grading-type-content">
                      <h4>Por Puntajes</h4>
                      <p>Cada respuesta suma puntos. Al final se evalúa el total contra rangos predefinidos.</p>
                      <div className="grading-type-example">
                        Ej: 0-5 pts = Grado A, 6-15 pts = Grado B, 16+ pts = Grado C
                      </div>
                    </div>
                  </div>

                  <div 
                    className={`grading-type-card ${configuracionGradificacion.tipoGradificacion === 'grados_directos' ? 'active' : ''}`}
                    onClick={() => actualizarConfiguracion('tipoGradificacion', 'grados_directos')}
                  >
                    <div className="grading-type-icon">🎯</div>
                    <div className="grading-type-content">
                      <h4>Grados Directos</h4>
                      <p>Las respuestas asignan grados directamente y pueden finalizar la evaluación.</p>
                      <div className="grading-type-example">
                        Ej: "Pantalla rota" → Grado C + Finalizar evaluación
                      </div>
                    </div>
                  </div>

                  <div 
                    className={`grading-type-card ${configuracionGradificacion.tipoGradificacion === 'hibrida' ? 'active' : ''}`}
                    onClick={() => actualizarConfiguracion('tipoGradificacion', 'hibrida')}
                  >
                    <div className="grading-type-icon">🔀</div>
                    <div className="grading-type-content">
                      <h4>Híbrida</h4>
                      <p>Combina ambos métodos: algunas respuestas suman puntos, otras asignan grados directos.</p>
                      <div className="grading-type-example">
                        Máxima flexibilidad para casos complejos
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Configuración de Grados */}
              <div className="form-group">
                <div className="section-header">
                  <label className="form-label">
                    Grados de Evaluación <span className="required">*</span>
                  </label>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={agregarGrado}
                  >
                    <span className="btn-icon">➕</span>
                    Agregar Grado
                  </button>
                </div>
                
                <div className="grades-list">
                  {configuracionGradificacion.grados.map((grado, index) => (
                    <div key={grado.id} className="grade-item">
                      <div className="grade-header">
                        <div 
                          className="grade-color-indicator"
                          style={{ backgroundColor: grado.color }}
                        ></div>
                        <span className="grade-id">Grado {grado.id}</span>
                        <div className="grade-actions">
                          {configuracionGradificacion.grados.length > 1 && (
                            <button
                              type="button"
                              className="btn-icon-small danger"
                              onClick={() => eliminarGrado(grado.id)}
                              title="Eliminar grado"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </div>
                      
                      <div className="grade-content">
                        <div className="form-row">
                          <div className="form-group-inline">
                            <label className="form-label-small">Nombre</label>
                            <input
                              type="text"
                              className="form-input"
                              value={grado.nombre}
                              onChange={(e) => actualizarGrado(grado.id, 'nombre', e.target.value)}
                              placeholder="Ej: Excelente Estado"
                            />
                          </div>
                          
                          <div className="form-group-inline">
                            <label className="form-label-small">Color</label>
                            <input
                              type="color"
                              className="form-input form-input-color"
                              value={grado.color}
                              onChange={(e) => actualizarGrado(grado.id, 'color', e.target.value)}
                            />
                          </div>
                        </div>
                        
                        <div className="form-group-inline">
                          <label className="form-label-small">Descripción</label>
                          <textarea
                            className="form-textarea form-textarea-small"
                            value={grado.descripcion}
                            onChange={(e) => actualizarGrado(grado.id, 'descripcion', e.target.value)}
                            placeholder="Describe qué representa este grado..."
                            rows={2}
                          />
                        </div>

                        {/* Configuración de thresholds para puntajes e híbrida */}
                        {(configuracionGradificacion.tipoGradificacion === 'puntajes' || 
                          configuracionGradificacion.tipoGradificacion === 'hibrida') && (
                          <div className="threshold-config">
                            <label className="form-label-small">Rango de Puntajes</label>
                            <div className="threshold-inputs">
                              <input
                                type="number"
                                className="form-input form-input-small"
                                value={configuracionGradificacion.thresholds.find(t => t.gradoId === grado.id)?.min || 0}
                                onChange={(e) => actualizarThreshold(grado.id, 'min', e.target.value)}
                                min="0"
                                placeholder="Mín"
                              />
                              <span className="threshold-separator">a</span>
                              <input
                                type="number"
                                className="form-input form-input-small"
                                value={configuracionGradificacion.thresholds.find(t => t.gradoId === grado.id)?.max || 999}
                                onChange={(e) => actualizarThreshold(grado.id, 'max', e.target.value)}
                                min="0"
                                placeholder="Máx"
                              />
                              <span className="threshold-help">puntos</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Vista previa del sistema */}
              <div className="form-group">
                <label className="form-label">Vista Previa del Sistema</label>
                <div className="system-preview">
                  <div className="preview-header">
                    <span className="preview-title">
                      Tipo: {
                        configuracionGradificacion.tipoGradificacion === 'puntajes' ? '📊 Por Puntajes' :
                        configuracionGradificacion.tipoGradificacion === 'grados_directos' ? '🎯 Grados Directos' :
                        '🔀 Híbrida'
                      }
                    </span>
                  </div>
                  
                  <div className="grades-preview">
                    {configuracionGradificacion.grados.map(grado => (
                      <div key={grado.id} className="grade-preview-item">
                        <div 
                          className="grade-preview-color"
                          style={{ backgroundColor: grado.color }}
                        ></div>
                        <div className="grade-preview-content">
                          <div className="grade-preview-name">{grado.nombre}</div>
                          {(configuracionGradificacion.tipoGradificacion === 'puntajes' ||
                            configuracionGradificacion.tipoGradificacion === 'hibrida') && (
                            <div className="grade-preview-range">
                              {configuracionGradificacion.thresholds.find(t => t.gradoId === grado.id)?.min || 0} - {configuracionGradificacion.thresholds.find(t => t.gradoId === grado.id)?.max || 999} puntos
                            </div>
                          )}
                          <div className="grade-preview-description">{grado.descripcion}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Botones de acción para configuración */}
              <div className="config-actions">
                <button 
                  className="btn btn-outline"
                  onClick={cancelar}
                  disabled={guardando}
                >
                  <span className="btn-icon">↶</span>
                  Cancelar
                </button>
                
                <button 
                  className={`btn btn-primary ${guardando ? 'loading' : ''}`}
                  onClick={guardarColeccion}
                  disabled={guardando}
                >
                  <span className="btn-icon">{guardando ? '⏳' : '💾'}</span>
                  {guardando ? 'Guardando...' : 'Guardar Configuración'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Confirmación de guardado */}
      {mostrarConfirmacion && (
        <div className="confirmation-toast">
          <span className="toast-icon">✅</span>
          ¡Colección guardada exitosamente!
        </div>
      )}

      {/* Modal Nueva Colección */}
      {mostrarModalNuevaColeccion && (
        <div className="modal-overlay">
          <div className="modal-content modal-nueva-coleccion">
            <div className="modal-header">
              <h3>📝 Nueva Colección de Preguntas</h3>
              <p>Crea una nueva colección para evaluar dispositivos de una categoría específica</p>
              <button
                className="modal-close-btn"
                onClick={() => setMostrarModalNuevaColeccion(false)}
                title="Cerrar"
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">
                  Nombre de la Colección <span className="required">*</span>
                </label>
                <input
                  type="text"
                  className={`form-input ${erroresModal.nombre ? 'error' : ''}`}
                  value={datosNuevaColeccion.nombre}
                  onChange={(e) => {
                    setDatosNuevaColeccion(prev => ({...prev, nombre: e.target.value}));
                    if (erroresModal.nombre) {
                      setErroresModal(prev => ({...prev, nombre: ''}));
                    }
                  }}
                  placeholder="Ej: Evaluación de Smartphones"
                  maxLength={100}
                />
                {erroresModal.nombre && (
                  <div className="error-message">{erroresModal.nombre}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Descripción (Opcional)
                </label>
                <textarea
                  className="form-textarea"
                  value={datosNuevaColeccion.descripcion}
                  onChange={(e) => setDatosNuevaColeccion(prev => ({...prev, descripcion: e.target.value}))}
                  placeholder="Describe el propósito y alcance de esta colección..."
                  rows={3}
                  maxLength={300}
                />
                <div className="char-counter">
                  {datosNuevaColeccion.descripcion.length}/300 caracteres
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Categoría <span className="required">*</span>
                </label>
                <select
                  className={`form-input ${erroresModal.categoria ? 'error' : ''}`}
                  value={datosNuevaColeccion.categoria}
                  onChange={(e) => {
                    setDatosNuevaColeccion(prev => ({...prev, categoria: e.target.value}));
                    if (erroresModal.categoria) {
                      setErroresModal(prev => ({...prev, categoria: ''}));
                    }
                  }}
                  disabled={cargandoCategorias || errorCategorias}
                >
                  <option value="">Selecciona una categoría</option>
                  {categorias.map(categoria => (
                    <option key={categoria.id} value={categoria.id}>
                      {categoria.name}
                    </option>
                  ))}
                </select>
                {erroresModal.categoria && (
                  <div className="error-message">{erroresModal.categoria}</div>
                )}
                {cargandoCategorias && (
                  <div className="loading-text">Cargando categorías...</div>
                )}
                {!cargandoCategorias && errorCategorias && (
                  <div className="error-message">
                    No se pudieron cargar las categorías.
                    <button
                      type="button"
                      style={{marginLeft: '8px', textDecoration: 'underline', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer'}}
                      onClick={cargarCategorias}
                    >
                      Reintentar
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-outline"
                onClick={() => setMostrarModalNuevaColeccion(false)}
              >
                <span className="btn-icon">↶</span>
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={crearNuevaColeccionDesdeModal}
              >
                <span className="btn-icon">✨</span>
                Crear Colección
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Simulador Vista Previa */}
      {mostrarSimulador && (
        <SimuladorVista
          tituloGeneral={tituloGeneral}
          preguntas={preguntas}
          configuracionGradificacion={configuracionGradificacion}
          onCerrar={() => setMostrarSimulador(false)}
        />
      )}
    </div>
  );
}

export default GestionPreguntas;